import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Pencil,
  Trash2,
  Power,
  Eye,
  Download,
  FileText,
  X,
  RefreshCcw,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import { EmployeesService } from "../../../services/employees.service";
import { warehouseService } from "../../../services/warehouse.service";
import { env } from "../../../config/env";
import { getCurrentDashboard } from "../../AdminAuth/components/authStore";
import ConfirmModal from "../components/ConfirmModal";

const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";

function normalizeWarehouseRecord(warehouse = {}) {
  const id = String(warehouse?.id || warehouse?._id || "").trim();
  const name = String(
    warehouse?.name || warehouse?.warehouse_name || warehouse?.warehouseName || ""
  ).trim();
  const code = String(warehouse?.code || warehouse?.warehouse_code || "").trim();
  const city = String(warehouse?.city || warehouse?.address?.city || "").trim();
  const state = String(warehouse?.state || warehouse?.address?.state || "").trim();

  return { id, name, code, city, state };
}

function buildWarehouseLookup(warehouses = []) {
  return (Array.isArray(warehouses) ? warehouses : []).reduce((lookup, item) => {
    const normalized = normalizeWarehouseRecord(item);
    if (normalized.id) lookup[normalized.id] = normalized;
    return lookup;
  }, {});
}

function resolveEmployeeWarehouse(employee, warehouseLookup = {}) {
  const raw = employee?._raw || {};
  const assignedWarehouseId = String(
    employee?.assignedWarehouseId ||
      raw?.assigned_warehouse_id ||
      raw?.assignedWarehouseId ||
      ""
  ).trim();
  const directWarehouseName = String(
    employee?.assignedWarehouseName ||
      raw?.assigned_warehouse_name ||
      raw?.assignedWarehouseName ||
      raw?.warehouse_name ||
      raw?.warehouseName ||
      ""
  ).trim();
  const type = String(
    employee?.userType || employee?.role || employee?._source || ""
  ).toLowerCase();
  const isWarehouseEmployee = type.includes("warehouse_employee");
  const allowedIds = Array.isArray(employee?.allowedWarehouseIds)
    ? employee.allowedWarehouseIds.map(String).filter(Boolean)
    : [];

  const formatWarehouse = (warehouse, fallbackId = "") => {
    if (!warehouse) {
      if (directWarehouseName) return directWarehouseName;
      return fallbackId ? `Warehouse ID: ${fallbackId}` : "";
    }

    const name = warehouse.name || directWarehouseName || "Warehouse";
    const code = warehouse.code ? ` (${warehouse.code})` : "";
    return `${name}${code}`;
  };

  if (assignedWarehouseId || directWarehouseName) {
    const warehouse = warehouseLookup[assignedWarehouseId];
    const label = formatWarehouse(warehouse, assignedWarehouseId);
    const location = [warehouse?.city, warehouse?.state].filter(Boolean).join(", ");

    return {
      label: label || assignedWarehouseId || directWarehouseName,
      title: [label, location, assignedWarehouseId ? `ID: ${assignedWarehouseId}` : ""]
        .filter(Boolean)
        .join(" • "),
      missing: false,
    };
  }

  if (allowedIds.length > 0) {
    const labels = allowedIds.map((id) => {
      const warehouse = warehouseLookup[id];
      return formatWarehouse(warehouse, id) || id;
    });

    return {
      label:
        labels.length <= 2
          ? labels.join(", ")
          : `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`,
      title: labels.join(", "),
      missing: false,
    };
  }

  if (isWarehouseEmployee) {
    return {
      label: "Not assigned",
      title: "This warehouse employee has no assigned warehouse.",
      missing: true,
    };
  }

  return { label: "-", title: "No warehouse access assigned", missing: false };
}


function buildFileUrl(path) {
  if (!path) return "";
  if (String(path).startsWith("http")) return path;

  const base =
    env?.AUTH_BASE_URL ||
    env?.REACT_APP_AUTH_BASE_URL ||
    env?.API_BASE_URL ||
    "";

  if (!base) return path;

  const cleanBase = String(base).replace(/\/$/, "");
  const cleanPath = String(path).startsWith("/") ? path : `/${path}`;

  return `${cleanBase}${cleanPath}`;
}

function Card({ children, className = "" }) {
  return (
    <div
      className={"rounded-[14px] bg-white " + className}
      style={{ border: `1px solid ${BORDER}` }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[19px] sm:text-[19px] font-semibold text-slate-700">{children}</div>;
}

function SmallLabel({ children }) {
  return <div className="text-[12px] text-slate-500">{children}</div>;
}

function SmallValue({ children, className = "" }) {
  return <div className={"text-[12px] text-slate-400 " + className}>{children}</div>;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <SmallLabel>{label}</SmallLabel>
      <SmallValue>{value || "-"}</SmallValue>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-[105px_1fr] sm:grid-cols-[120px_1fr] lg:grid-cols-[130px_1fr] gap-2 sm:gap-3 py-2">
      <div className="text-[12px] font-medium text-slate-500">{label}</div>
      <div className="break-words text-[13px] text-slate-400">{value || "-"}</div>
    </div>
  );
}

function StatusModal({
  open,
  title,
  message,
  confirmText = "OK",
  onClose,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/35 p-4">
      <div
        className="relative w-full max-w-[360px] rounded-[20px] bg-white px-6 pb-6 pt-5 shadow-[0_20px_70px_rgba(15,23,42,0.20)]"
        style={{ border: `1px solid ${BORDER}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div
            className="mb-4 flex h-[82px] w-[82px] items-center justify-center rounded-full"
            style={{ background: "rgba(216,106,120,0.12)" }}
          >
            <AlertTriangle size={44} color={PINK} strokeWidth={2.2} />
          </div>

          <div className="text-[20px] font-bold text-slate-700">{title}</div>
          <div className="mt-3 max-w-[260px] text-[13px] leading-6 text-slate-500">
            {message}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 inline-flex h-[38px] min-w-[120px] items-center justify-center rounded-[8px] px-5 text-[12px] font-semibold text-white transition"
            style={{ background: PINK }}
            onMouseEnter={(e) => (e.currentTarget.style.background = PINK_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.background = PINK)}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}



async function fetchFileBlob(url) {
  const res = await fetch(url, { method: "GET" });

  if (!res.ok) {
    throw new Error("File not found");
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const text = await res.text();
    if (/not found/i.test(text) || /success\"\s*:\s*false/i.test(text)) {
      throw new Error("File not found");
    }
    throw new Error("Invalid file response");
  }

  const blob = await res.blob();

  if (!blob || !blob.size) {
    throw new Error("Empty file");
  }

  return blob;
}

async function openFilePreview(url) {
  const blob = await fetchFileBlob(url);
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, "_blank", "noopener,noreferrer");

  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 60000);
}

async function downloadFile(url, filename = "download") {
  const blob = await fetchFileBlob(url);
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 1000);
}

function UploadSlot({ label, src, fileName, onPreview, onDownload }) {
  const fileUrl = buildFileUrl(src);
  const isPdf = String(src || "").toLowerCase().endsWith(".pdf");
  const [imageBroken, setImageBroken] = useState(false);

  const hasFile = Boolean(src && fileUrl);
  const showFilePreview = hasFile && !imageBroken;

  return (
    <div className="w-full">
      <div className="mb-2 text-[11px] text-slate-500">{label}</div>

      <div
        className="group relative flex h-[140px] sm:h-[150px] w-full items-center justify-center overflow-hidden rounded-[10px] bg-white"
        style={{ border: `1px solid ${BORDER}` }}
      >
        {hasFile ? (
          <>
            <div className="absolute right-2 top-2 z-10 flex items-center gap-2 opacity-100 sm:opacity-0 sm:transition sm:duration-200 sm:group-hover:opacity-100">
              <button
                type="button"
                onClick={() => onPreview({ url: fileUrl, label, fileName })}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-slate-600 shadow-sm hover:bg-white hover:text-slate-800"
                title="Preview"
              >
                <Eye size={15} />
              </button>

              <button
                type="button"
                onClick={() => onDownload({ url: fileUrl, label, fileName })}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-slate-600 shadow-sm hover:bg-white hover:text-slate-800"
                title="Download"
              >
                <Download size={15} />
              </button>
            </div>

            {isPdf ? (
              <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                <FileText size={34} />
                <div className="text-[11px]">PDF Document</div>
              </div>
            ) : showFilePreview ? (
              <img
                src={fileUrl}
                alt={label}
                className="h-full w-full object-cover"
                onError={() => setImageBroken(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 px-4 text-center text-slate-400">
                <FileText size={30} />
                <div className="text-[11px] font-medium">Preview not available</div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1">
            <div className="text-[28px] font-semibold" style={{ color: PINK }}>
              +
            </div>
            <div className="text-[11px] text-slate-400">No file uploaded</div>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityLogModal({ open, rows, loading, onClose, onRefresh, error }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-[980px] overflow-hidden rounded-[22px] bg-white shadow-[0_25px_80px_rgba(15,23,42,0.22)]">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 sm:px-6 py-4">
          <div>
            <div className="text-[18px] sm:text-[20px] font-semibold text-slate-700">Activity Logs</div>
            <div className="mt-1 text-[12px] text-slate-400">Latest employee activity from admin logs</div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 px-3 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
            >
              <RefreshCcw size={14} />
              Refresh
            </button>

            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
              aria-label="Close activity logs"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(85vh-84px)] overflow-auto p-4 sm:p-6">
          <div className="overflow-hidden rounded-[16px] border border-slate-200">
            <table className="w-full border-separate border-spacing-0">
              <thead className="sticky top-0 bg-white">
                <tr>
                  {[
                    { key: "date", label: "Date" },
                    { key: "time", label: "Time" },
                    { key: "service", label: "Service" },
                    { key: "activity", label: "Activity" },
                  ].map((h) => (
                    <th
                      key={h.key}
                      className="border-b border-slate-200 px-3 sm:px-4 py-3 text-left text-[12px] font-semibold"
                      style={{ color: PINK }}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[13px] text-slate-500">
                      Loading activity logs...
                    </td>
                  </tr>
                ) : rows.length ? (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/70">
                      <td className="border-b border-slate-100 px-3 sm:px-4 py-4 text-[12px] text-slate-500">{row.date}</td>
                      <td className="border-b border-slate-100 px-3 sm:px-4 py-4 text-[12px] text-slate-500">{row.time}</td>
                      <td className="border-b border-slate-100 px-3 sm:px-4 py-4 text-[12px] text-slate-500">{row.service || "-"}</td>
                      <td className="border-b border-slate-100 px-3 sm:px-4 py-4 text-[12px] text-slate-600">
                        <div className="font-medium text-slate-600">{row.activity}</div>
                        {row.action ? <div className="mt-1 text-[11px] text-slate-400">Action: {row.action}</div> : null}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[13px] text-slate-400">
                      {error || "No activity logs found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentDashboard = getCurrentDashboard();
  const isWarehouseDashboard = currentDashboard === "warehouse";

  const [employee, setEmployee] = useState(null);
  const [warehouseLookup, setWarehouseLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [allActivityRows, setAllActivityRows] = useState([]);
  const [activityModalOpen, setActivityModalOpen] = useState(false);

  const [statusModal, setStatusModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmModal, setConfirmModal] = useState({
  open: false,
  kind: "",
});

  const profileAvatar = employee?.avatar || employee?.documents?.employeePhoto || "";

  const warehouseDisplay = useMemo(
    () => resolveEmployeeWarehouse(employee, warehouseLookup),
    [employee, warehouseLookup]
  );

  const showStatusModal = (title, message) => {
    setStatusModal({
      open: true,
      title,
      message,
    });
  };

  const closeStatusModal = () => {
    setStatusModal((prev) => ({ ...prev, open: false }));
  };

  const closeConfirmModal = () => {
  if (saving) return;

  setConfirmModal({
    open: false,
    kind: "",
  });
};

const openToggleConfirm = () => {
  if (!employee || saving) return;

  setConfirmModal({
    open: true,
    kind: employee.status === "Active" ? "deactivate" : "activate",
  });
};

const openDeleteConfirm = () => {
  if (!employee || saving) return;

  setConfirmModal({
    open: true,
    kind: "delete",
  });
};

  const loadEmployee = async () => {
    try {
      setLoading(true);
      setError("");
      const [found, warehouses] = await Promise.all([
        EmployeesService.getEmployeeById(id),
        warehouseService.listAllWarehouses().catch(() => []),
      ]);

      setEmployee(found || null);
      setWarehouseLookup(buildWarehouseLookup(warehouses));
    } catch (e) {
      setError(e?.message || "Failed to load employee");
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async ({ full = false } = {}) => {
    if (!id) return;

    try {
      setActivityLoading(true);
      setActivityError("");

      const result = await EmployeesService.getEmployeeActivityLogs(id, {
        page: 1,
        limit: full ? 50 : 6,
      });

      const items = Array.isArray(result?.items) ? result.items : [];
      if (full) setAllActivityRows(items);
      else setActivityRows(items);
    } catch (e) {
      const message = e?.message || "Failed to load activity logs";
      setActivityError(message);
      if (full) setAllActivityRows([]);
      else setActivityRows([]);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    loadEmployee();
    loadActivityLogs();
  }, [id]);

  const initials = useMemo(() => {
    const parts = String(employee?.name || "Employee").trim().split(/\s+/);
    return (parts[0]?.[0] || "E") + (parts[1]?.[0] || "D");
  }, [employee?.name]);

  const onboardingDocs = employee?._raw?.onboarding?.documents || {};
  const onboardingPhotos = employee?._raw?.onboarding?.photos || {};

  const documentsList = [
    {
      title: "Employee Photo",
      src: employee?.documents?.employeePhoto || onboardingPhotos?.employeePhoto || "",
      fileName: "employee-photo",
    },
    {
      title: "Aadhaar Card",
      src: employee?.documents?.aadhaarCard || onboardingDocs?.aadhaar?.url || "",
      fileName: onboardingDocs?.aadhaar?.originalName || "aadhaar-card",
    },
    {
      title: "Pan Card",
      src: employee?.documents?.panCard || onboardingDocs?.pan?.url || "",
      fileName: onboardingDocs?.pan?.originalName || "pan-card",
    },
    {
      title: "Photo 2",
      src:
        employee?.documents?.photo2 ||
        onboardingDocs?.photo2?.url ||
        onboardingPhotos?.photo2?.url ||
        "",
      fileName:
        onboardingDocs?.photo2?.originalName ||
        onboardingPhotos?.photo2?.originalName ||
        "photo-2",
    },
  ];

  const handleFilePreview = async ({ url, label }) => {
    try {
      await openFilePreview(url);
    } catch (e) {
      showStatusModal(
        "Preview Not Available",
        `${label} Preview is not opening. The file is missing or an invalid response is being returned.`
      );
    }
  };

  const handleFileDownload = async ({ url, label, fileName }) => {
    try {
      await downloadFile(url, fileName || label || "download");
    } catch (e) {
      showStatusModal(
        "Download Failed",
        `${label} Download is not working. The file is not available on the server or the response is invalid.`
      );
    }
  };

  const handleEditEmployee = () => {
    if (!employee?.id) return;
    navigate(`/admin/dashboard/employees/${employee.id}/edit`);
  };

  const handleToggleActive = async () => {
  if (!employee || saving) return;

  try {
    setSaving(true);

    if (employee.status === "Active") {
      await EmployeesService.deactivateEmployee(employee.id);
      showStatusModal("Employee Deactivated", "Employee has been deactivated successfully.");
    } else {
      await EmployeesService.activateEmployee(employee.id);
      showStatusModal("Employee Activated", "Employee has been activated successfully.");
    }

    closeConfirmModal();
    await loadEmployee();
  } catch (e) {
    showStatusModal("Action Failed", e?.message || "Failed to update employee status");
  } finally {
    setSaving(false);
  }
};

  const handleDeleteEmployee = async () => {
  if (!employee || saving) return;

  try {
    setSaving(true);

    await EmployeesService.deleteAdminEmployee(employee.id);

    closeConfirmModal();
    navigate("/admin/dashboard/employees");
  } catch (e) {
    showStatusModal("Delete Failed", e?.message || "Failed to delete employee");
  } finally {
    setSaving(false);
  }
};

  // const handleOpenActivityModal = async () => {
  //   setActivityModalOpen(true);
  //   await loadActivityLogs({ full: true });
  // };

  return (
    <div className="w-full px-3 sm:px-4 lg:px-6">
      {/* <ActivityLogModal
        open={activityModalOpen}
        rows={allActivityRows}
        loading={activityLoading}
        error={activityError}
        onClose={() => setActivityModalOpen(false)}
        onRefresh={() => loadActivityLogs({ full: true })}
      /> */}

      <StatusModal
        open={statusModal.open}
        title={statusModal.title}
        message={statusModal.message}
        onClose={closeStatusModal}
      />

      <ConfirmModal
  open={
    confirmModal.open &&
    (confirmModal.kind === "activate" || confirmModal.kind === "deactivate")
  }
  icon="ban"
  variant="confirm"
  title={
    confirmModal.kind === "activate"
      ? `Are you sure you want to Activate ${employee?.name || ""}?`
      : `Are you sure you want to Deactivate ${employee?.name || ""}?`
  }
  description={
    confirmModal.kind === "activate"
      ? "This employee will be able to sign in again and use the admin panel."
      : "This employee will not be able to sign in until activated again."
  }
  onClose={closeConfirmModal}
  onConfirm={handleToggleActive}
  cancelText="NO"
  confirmText={saving ? "PLEASE WAIT..." : "YES"}
/>

<ConfirmModal
  open={confirmModal.open && confirmModal.kind === "delete"}
  icon="trash"
  variant="confirm"
  title={`Are you sure you want to Delete ${employee?.name || ""}?`}
  description="This will permanently remove the employee record from the list."
  onClose={closeConfirmModal}
  onConfirm={handleDeleteEmployee}
  cancelText="NO"
  confirmText={saving ? "DELETING..." : "YES"}
/>

      {loading ? (
        <div className="mx-auto w-full max-w-[1080px] p-4 sm:p-6 text-[13px] text-slate-600">Loading...</div>
      ) : error ? (
        <div className="mx-auto w-full max-w-[1080px] p-4 sm:p-6 text-[13px] text-red-600">{error}</div>
      ) : !employee ? (
        <div className="mx-auto w-full max-w-[1080px] p-4 sm:p-6 text-[13px] text-slate-600">Employee not found.</div>
      ) : (
        <div className="mx-auto w-full max-w-[1080px] xl:max-w-[1400px] 2xl:max-w-[1980px]">
          <div className="mb-5 sm:mb-6">
            <div className="flex items-center gap-3">
  <button
    type="button"
    onClick={() => navigate("/admin/dashboard/employees")}
    className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[#f2c6cc] bg-white text-[#d86a78] transition hover:bg-[#fff1f3]"
    title="Back"
  >
    <ArrowLeft size={17} />
  </button>

  <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
    EMPLOYEE DETAILS
  </h1>
</div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(340px,390px)] xl:gap-6">
            <div className="space-y-5 sm:space-y-6">
              <Card className="p-4 sm:p-5 lg:p-6">
                <div className="relative">
                  {!isWarehouseDashboard ? (
                    <button
                      type="button"
                      onClick={handleEditEmployee}
                      className="absolute right-0 top-0 rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                      aria-label="Edit"
                      title="Edit Employee"
                      disabled={saving}
                    >
                      <Pencil size={16} />
                    </button>
                  ) : null}

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 pr-8 sm:pr-10">
                    {profileAvatar ? (
                      <div className="h-[82px] w-[82px] sm:h-[86px] sm:w-[86px] overflow-hidden rounded-full border border-[#f2c6cc] shrink-0">
                        <img src={buildFileUrl(profileAvatar)} alt={employee.name} className="h-full w-full object-cover" />
                      </div>
                    ) : (
                      <div
                        className="flex h-[82px] w-[82px] sm:h-[86px] sm:w-[86px] items-center justify-center rounded-full shrink-0"
                        style={{ background: "rgba(216,106,120,0.18)" }}
                      >
                        <div
                          className="flex h-[64px] w-[64px] sm:h-[68px] sm:w-[68px] items-center justify-center rounded-full text-[18px] font-bold"
                          style={{ background: "rgba(216,106,120,0.25)", color: "#2f2f2f" }}
                        >
                          {initials}
                        </div>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold text-slate-700 break-words">{employee.name}</div>
                      <div className="mt-1 text-[15px] text-slate-400 break-all">{employee.email}</div>
                      <div className="mt-1 text-[15px] text-slate-400 break-words">{employee.phone}</div>

                      {!isWarehouseDashboard ? (
                        <div className="mt-3 flex items-center gap-3 text-slate-400">
                          <button
                            type="button"
                            onClick={openToggleConfirm}
                            className="rounded-md p-1.5 hover:bg-slate-50 hover:text-slate-600"
                            aria-label={employee.status === "Active" ? "Deactivate employee" : "Activate employee"}
                            title={employee.status === "Active" ? "Deactivate Employee" : "Activate Employee"}
                            disabled={saving}
                          >
                            <Power size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={openDeleteConfirm}
                            className="rounded-md p-1.5 hover:bg-slate-50 hover:text-slate-600"
                            aria-label="Delete"
                            title="Delete Employee"
                            disabled={saving}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ) : null}

                      <div className="mt-2 text-[11px] text-slate-500">
                        {saving
                          ? "Updating..."
                          : employee.status === "Active"
                            ? "Employee is Active"
                            : "Employee is Inactive"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 h-px w-full" style={{ background: "rgba(242,198,204,0.7)" }} />

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 lg:gap-x-8 gap-y-3">
                    <InfoRow label="Employee ID :-" value={employee.employeeId} />
                    <InfoRow label="Role :-" value={employee.role} />
                    <InfoRow label="Location :-" value={employee.location} />
                    <InfoRow label="Status :-" value={employee.status} />
                    <InfoRow label="DOB :-" value={employee.dob} />
                    <InfoRow label="Onboarding :-" value={employee.onboarding} />
                    <InfoRow label="Department :-" value={employee.department} />
                    <InfoRow label="Warehouse :-" value={warehouseDisplay.label} />
                    <InfoRow label="Joining Date :-" value={employee.joiningDate} />
                    <InfoRow label="Last Login :-" value={employee.lastLogin} />
                    <InfoRow label="User Type :-" value={employee.userType} />
                    <InfoRow label="Approval :-" value={employee.approvalLabel || employee.approvalStatus} />
                    <InfoRow label="Onboarding Step :-" value={employee.onboardingStep} />
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-5 lg:p-6">
                <div className="relative">
                  {/* <button
                    type="button"
                    onClick={() => alert("Edit documents")}
                    className="absolute right-0 top-0 rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                    aria-label="Edit"
                  >
                    <Pencil size={16} />
                  </button> */}

                  <SectionTitle>Documents Preview</SectionTitle>
                  <div className="mt-3 h-px w-full" style={{ background: "rgba(242,198,204,0.7)" }} />

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    {documentsList.map((item) => (
                      <UploadSlot
                        key={item.title}
                        label={item.title}
                        src={item.src}
                        fileName={item.fileName}
                        onPreview={handleFilePreview}
                        onDownload={handleFileDownload}
                      />
                    ))}
                  </div>
                </div>
              </Card>

              <Card className="p-4 sm:p-5 lg:p-6">
  <SectionTitle>Onboarding Details</SectionTitle>

  <div
    className="mt-3 h-px w-full"
    style={{ background: "rgba(242,198,204,0.7)" }}
  />

  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
    <div
      className="rounded-[14px] bg-[#fffafb] p-4"
      style={{ border: `1px solid ${BORDER}` }}
    >
      <div className="mb-4 text-[13px] font-semibold text-slate-700">
        Status Info
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-medium text-slate-500">
            Step
          </span>
          <span
            className="rounded-full px-3 py-1 text-[12px] font-semibold"
            style={{
              background: "rgba(216,106,120,0.12)",
              color: PINK,
            }}
          >
            {employee.onboardingStep || "-"}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-medium text-slate-500">
            Completed
          </span>
          <span className="text-[12px] text-slate-400">
            {employee.onboarding || "-"}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-medium text-slate-500">
            Excel Confirmed
          </span>
          <span className="text-[12px] text-slate-400">
            {employee.excelKnowledgeConfirmed ? "Yes" : "No"}
          </span>
        </div>
      </div>
    </div>

    <div
      className="rounded-[14px] bg-[#fffafb] p-4"
      style={{ border: `1px solid ${BORDER}` }}
    >
      <div className="mb-4 text-[13px] font-semibold text-slate-700">
        Approval
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-medium text-slate-500">
            Status
          </span>
          <span
            className="rounded-full px-3 py-1 text-[12px] font-semibold"
            style={{
              background: "rgba(34,197,94,0.12)",
              color: "#16a34a",
            }}
          >
            {employee.approvalLabel || employee.approvalStatus || "-"}
          </span>
        </div>

        <div>
          <div className="mb-1 text-[12px] font-medium text-slate-500">
            Approval Note
          </div>
          <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
            {employee.approvalNote || "-"}
          </div>
        </div>

        <div>
          <div className="mb-1 text-[12px] font-medium text-slate-500">
            Onboarding Notes
          </div>
          <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
            {employee.onboardingNotes || "-"}
          </div>
        </div>
      </div>
    </div>

    <div
      className="rounded-[14px] bg-[#fffafb] p-4 sm:col-span-2 xl:col-span-1"
      style={{ border: `1px solid ${BORDER}` }}
    >
      <div className="mb-4 text-[13px] font-semibold text-slate-700">
        Address
      </div>

      <div className="space-y-3">
        <div>
          <div className="mb-1 text-[12px] font-medium text-slate-500">
            Address Line 1
          </div>
          <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
            {employee.onboardingAddress?.line1 || "-"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1 text-[12px] font-medium text-slate-500">
              City
            </div>
            <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
              {employee.onboardingAddress?.city || "-"}
            </div>
          </div>

          <div>
            <div className="mb-1 text-[12px] font-medium text-slate-500">
              State
            </div>
            <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
              {employee.onboardingAddress?.state || "-"}
            </div>
          </div>

          <div>
            <div className="mb-1 text-[12px] font-medium text-slate-500">
              Country
            </div>
            <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
              {employee.onboardingAddress?.country || "-"}
            </div>
          </div>

          <div>
            <div className="mb-1 text-[12px] font-medium text-slate-500">
              Pincode
            </div>
            <div className="rounded-[10px] bg-white px-3 py-2 text-[12px] text-slate-400">
              {employee.onboardingAddress?.pincode || "-"}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</Card>
            </div>

            <div className="space-y-5 sm:space-y-6 xl:justify-self-start xl:w-full xl:max-w-[380px]">
              <Card className="p-4 sm:p-5 lg:p-6">
                <SectionTitle>Profile & Address Details</SectionTitle>
                <div className="mt-3 h-px w-full" style={{ background: "rgba(242,198,204,0.7)" }} />

                <div className="mt-4 space-y-1">
                  <DetailRow label="Designation" value={employee.designation} />
                  <DetailRow label="Gender" value={employee.gender} />
                  <DetailRow label="Bio" value={employee.bio} />
                  <DetailRow label="Profile Notes" value={employee.profileNotes} />
                  <DetailRow label="City" value={employee.address?.city} />
                  <DetailRow label="Line 1" value={employee.address?.line1} />
                  <DetailRow label="Line 2" value={employee.address?.line2} />
                  <DetailRow label="State" value={employee.address?.state} />
                  <DetailRow label="Country" value={employee.address?.country} />
                  <DetailRow label="Pincode" value={employee.address?.pincode} />
                </div>
              </Card>

              <Card className="p-4 sm:p-5 lg:p-6">
                <SectionTitle>Emergency Contact</SectionTitle>
                <div className="mt-3 h-px w-full" style={{ background: "rgba(242,198,204,0.7)" }} />

                <div className="mt-4 space-y-1">
                  <DetailRow label="Name" value={employee.emergencyContact?.name} />
                  <DetailRow label="Phone" value={employee.emergencyContact?.phone} />
                  <DetailRow label="Relation" value={employee.emergencyContact?.relation} />
                </div>
              </Card>

              <Card className="p-4 sm:p-5 lg:p-6">
                <SectionTitle>Reference Contacts</SectionTitle>
                <div className="mt-3 h-px w-full" style={{ background: "rgba(242,198,204,0.7)" }} />

                <div className="mt-4 space-y-4">
                  {employee.referenceContacts?.length ? (
                    employee.referenceContacts.map((item, index) => (
                      <div key={index} className="rounded-[10px] p-4" style={{ border: `1px solid ${BORDER}` }}>
                        <DetailRow label="Name" value={item?.name} />
                        <DetailRow label="Phone" value={item?.phone} />
                        <DetailRow label="Relation" value={item?.relation} />
                      </div>
                    ))
                  ) : (
                    <div className="text-[12px] text-slate-400">No reference contacts found.</div>
                  )}
                </div>
              </Card>

              {/* <Card className="p-6">
                <div className="text-center text-[18px] font-semibold text-slate-600">Activity Log</div>
                <div className="mx-auto mt-3 h-px w-full" style={{ background: "rgba(242,198,204,0.7)" }} />

                <div className="mt-4 overflow-hidden rounded-[10px]" style={{ border: "1px solid rgba(0,0,0,0.06)" }}>
                  <table className="w-full border-separate border-spacing-0">
                    <thead>
                      <tr>
                        {[
                          { key: "date", label: "Date" },
                          { key: "time", label: "Time" },
                          { key: "activity", label: "Activity" },
                        ].map((h) => (
                          <th
                            key={h.key}
                            className="border-b px-4 py-3 text-left text-[12px] font-semibold"
                            style={{ color: PINK, borderColor: "rgba(0,0,0,0.06)" }}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activityLoading ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-[12px] text-slate-400">
                            Loading activity logs...
                          </td>
                        </tr>
                      ) : activityRows.length ? (
                        activityRows.map((r) => (
                          <tr key={r.id} className="hover:bg-slate-50/40">
                            <td className="border-b px-4 py-4 text-[11px] text-slate-400" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                              {r.date}
                            </td>
                            <td className="border-b px-4 py-4 text-[11px] text-slate-400" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                              {r.time}
                            </td>
                            <td className="border-b px-4 py-4 text-[11px] text-slate-500" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                              <div className="font-medium text-slate-500">{r.activity}</div>
                              {r.service ? (
                                <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-slate-300">{r.service}</div>
                              ) : null}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-[12px] text-slate-400">
                            {activityError || "No activity logs found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-7 flex justify-center">
                  <button
                    type="button"
                    className="h-[34px] w-[140px] rounded-[10px] text-[12px] font-semibold text-white transition"
                    style={{ background: PINK }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = PINK_HOVER)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = PINK)}
                    onClick={handleOpenActivityModal}
                  >
                    View All
                  </button>
                </div>
              </Card> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}